import { Routes } from "@angular/router";
import { AuthGuard } from "./auth.guard";
import { loginGuard } from './login.guard';
import { TaskOperatorGuard } from './task-operator.guard';
import { adminAccessGuard } from './admin/admin-access.guard';

// Import only essential components that need to be loaded immediately
import { HomePageComponent } from '@app/pages/home/home-page/home-page.component';
import { ErrorPageComponent } from '@app/pages/system/error-page/error-page.component';
import { LoginPageComponent } from '@app/pages/auth/login-page/login-page.component';
import { RegisPageComponent } from '@app/pages/auth/regis-page/regis-page.component';

export const routes: Routes = [
    // Main home routes - loaded immediately
    {
        path: '',
        component: HomePageComponent,
        canActivate: [AuthGuard]
    },
    {
        path: 'home',
        component: HomePageComponent,
        canActivate: [AuthGuard]
    },
    {
        path: 'start',
        component: HomePageComponent,
        canActivate: [AuthGuard]
    },
    {
        path: 'public',
        component: HomePageComponent,
        canActivate: [AuthGuard]
    },
    
    // Authentication routes - loaded immediately for performance
    {
        path: 'login',
        component: LoginPageComponent,
        canActivate: [loginGuard]
    },
    {
        path: 'register',
        component: RegisPageComponent,
        canActivate: [loginGuard]
    },
    // Admin routes - lazy loaded
    {
        path: 'admin-manage-users',
        loadComponent: () => import('./admin/admin-manage-users/admin-manage-users.component').then(m => m.AdminManageUsersComponent),
        canActivate: [AuthGuard, adminAccessGuard],
        data: { permission: 'user_manage_permission' }
    },
    {
        path: 'admin-manage-organization-users',
        loadComponent: () => import('./admin/admin-manage-organization-users/admin-manage-organization-users.component').then(m => m.AdminManageOrganizationUsersComponent),
        canActivate: [AuthGuard, adminAccessGuard],
        data: { permission: 'user_manage_permission' }
    },
    {
        path: 'admin-manage-tasks',
        loadComponent: () => import('./admin/admin-manage-tasks/admin-manage-tasks.component').then(m => m.AdminManageTasksComponent),
        canActivate: [AuthGuard, adminAccessGuard],
        data: { permission: 'task_manage_permission' }
    },
    {
        path: 'admin-manage-admins-permission',
        loadComponent: () => import('./admin/admin-manage-admins-permission/admin-manage-admins-permission.component').then(m => m.AdminManageAdminsPermissionComponent),
        canActivate: [AuthGuard, adminAccessGuard],
        data: { superAdminOnly: true }
    },
    {
        path: 'permission-admin-detail-manage/:permissionId',
        loadComponent: () => import('./admin/permission-admin-detail-manage/permission-admin-detail-manage.component').then(m => m.PermissionAdminDetailManageComponent),
        canActivate: [AuthGuard, adminAccessGuard],
        data: { superAdminOnly: true }
    },
    {
        path: 'admin-manage-apply',
        loadComponent: () => import('./admin/admin-manage-apply/admin-manage-apply.component').then(m => m.AdminManageApplyComponent),
        canActivate: [AuthGuard, adminAccessGuard],
        data: { permission: 'subCategory_manage_permission' }
    },
    {
        path: 'admin-manage-identity-apply',
        loadComponent: () => import('./admin/admin-manage-identity-apply/admin-manage-identity-apply.component').then(m => m.AdminManageIdentityApplyComponent),
        canActivate: [AuthGuard, adminAccessGuard],
        data: { permission: 'user_manage_permission' }
    },
    {
        path: 'admin-manage-category',
        loadComponent: () => import('./admin/admin-manage-category/admin-manage-category.component').then(m => m.AdminManageCategoryComponent),
        canActivate: [AuthGuard, adminAccessGuard],
        data: { permission: 'category_manage_permission' }
    },
    {
        path: 'admin-manage-sub-category',
        loadComponent: () => import('./admin/admin-manage-sub-category/admin-manage-sub-category.component').then(m => m.AdminManageSubCategoryComponent),
        canActivate: [AuthGuard, adminAccessGuard],
        data: { permission: 'subCategory_manage_permission' }
    },
    {
        path: 'admin-announce',
        loadComponent: () => import('./admin/admin-announcement/admin-announcement.component').then(m => m.AdminAnnouncementComponent),
        canActivate: [AuthGuard, adminAccessGuard],
        data: { permission: 'announcement_manage_permission' }
    },
    {
        path: 'admin-create-announcement',
        loadComponent: () => import('./admin/admin-create-announcement/admin-create-announcement.component').then(m => m.AdminCreateAnnouncementComponent),
        canActivate: [AuthGuard, adminAccessGuard],
        data: { permission: 'announcement_manage_permission' }
    },
    {
        path: 'admin-manage-coin-record',
        loadComponent: () => import('./admin/admin-manage-coin-record/admin-manage-coin-record.component').then(m => m.AdminManageCoinRecordComponent),
        canActivate: [AuthGuard, adminAccessGuard],
        data: { permission: 'user_manage_permission' }
    },
    {
        path: 'admin-topup',
        loadComponent: () => import('./admin/admin-topup/admin-topup.component').then(m => m.AdminTopupComponent),
        canActivate: [AuthGuard, adminAccessGuard],
        data: { superAdminOnly: true }
    },
    {
        path: 'admin-manage',
        loadComponent: () => import('./admin/admin-manage/admin-manage.component').then(m => m.AdminManageComponent),
        canActivate: [AuthGuard, adminAccessGuard]
    },
    
    // Main application pages - lazy loaded individually for better chunking
    {
        path: 'charity-record',
        loadComponent: () => import('./pages/finance/charity-record/charity-record.component').then(m => m.CharityRecordComponent),
        canActivate: [AuthGuard]
    },
    {
        path: 'chart-page',
        loadComponent: () => import('./pages/dashboard/chart-page/chart-page.component').then(m => m.ChartPageComponent),
        canActivate: [AuthGuard, adminAccessGuard],
        data: { permission: 'task_manage_permission' }
    },
    {
        path: 'apply-schedule',
        loadComponent: () => import('./pages/apply/apply-schedule/apply-schedule.component').then(m => m.ApplyScheduleComponent),
        canActivate: [AuthGuard]
    },
    {
        path: 'category-apply',
        loadComponent: () => import('./pages/apply/category-apply/category-apply.component').then(m => m.CategoryApplyComponent),
        canActivate: [AuthGuard]
    },
    {
        path: 'edit-category/:categoryId',
        loadComponent: () => import('./pages/category/edit-category/edit-category.component').then(m => m.EditCategoryComponent),
        canActivate: [AuthGuard, adminAccessGuard],
        data: { permission: 'category_manage_permission' }
    },
    {
        path: 'edit-sub-category/:subCategoryId',
        loadComponent: () => import('./pages/category/edit-sub-category/edit-sub-category.component').then(m => m.EditSubCategoryComponent),
        canActivate: [AuthGuard, adminAccessGuard],
        data: { permission: 'subCategory_manage_permission' }
    },
    {
        path: 'create-category',
        loadComponent: () => import('./pages/category/create-category/create-category.component').then(m => m.CreateCategoryComponent),
        canActivate: [AuthGuard, adminAccessGuard],
        data: { permission: 'category_manage_permission' }
    },
    {
        path: 'create-sub-category',
        loadComponent: () => import('./pages/category/create-sub-category/create-sub-category.component').then(m => m.CreateSubCategoryComponent),
        canActivate: [AuthGuard, adminAccessGuard],
        data: { permission: 'subCategory_manage_permission' }
    },
    {
        path: 'personal-finance',
        loadComponent: () => import('./pages/finance/personal-finance/personal-finance.component').then(m => m.PersonalFinanceComponent),
        canActivate: [AuthGuard]
    },
    {
        path: 'account',
        loadComponent: () => import('./pages/account/account-page/account-page.component').then(m => m.AccountPageComponent),
        canActivate: [AuthGuard]
    },
    {
        path: 'create-task/:categoryId',
        loadComponent: () => import('./pages/task/create-task/create-task.component').then(m => m.CreateTaskComponent),
        canActivate: [AuthGuard]
    },
    {
        path: 'accept-task-history',
        loadComponent: () => import('./pages/task/accept-task-history/accept-task-history.component').then(m => m.AcceptTaskHistoryComponent),
        canActivate: [AuthGuard]
    },
    {
        path: 'create-task-history',
        loadComponent: () => import('./pages/task/create-task-history/create-task-history.component').then(m => m.CreateTaskHistoryComponent),
        canActivate: [AuthGuard]
    },
    {
        path: 'edit-task/:taskId',
        loadComponent: () => import('./pages/task/edit-task/edit-task.component').then(m => m.EditTaskComponent),
        canActivate: [AuthGuard]
    },
    {
        path: 'task-detail/:taskId',
        loadComponent: () => import('./pages/task/task-detail/task-detail.component').then(m => m.TaskDetailComponent),
        canActivate: [AuthGuard]
    },
    {
        path: 'task-detail-operator/:taskId',
        loadComponent: () => import('./pages/task/task-detail-for-operator/task-detail-for-operator.component').then(m => m.TaskDetailForOperatorComponent),
        canActivate: [AuthGuard, TaskOperatorGuard]
    },
    {
        path: 'task-group-detail/:groupId',
        loadComponent: () => import('./pages/task/task-group-detail/task-group-detail.component').then(m => m.TaskGroupDetailComponent),
        canActivate: [AuthGuard]
    },
    {
        path: 'finance-transfer-history',
        loadComponent: () => import('./pages/finance/finance-transfer-history/finance-transfer-history.component').then(m => m.FinanceTransferHistoryComponent),
        canActivate: [AuthGuard]
    },
    {
        path: 'announcement-detail/:announcementId',
        loadComponent: () => import('./pages/announcement/announcement-detail/announcement-detail.component').then(m => m.AnnouncementDetailComponent),
        canActivate: [AuthGuard]
    },
    {
        path: 'task-search-result/:keyword',
        loadComponent: () => import('./pages/task/task-search-result/task-search-result.component').then(m => m.TaskSearchResultComponent),
        canActivate: [AuthGuard]
    },
    {
        path: 'category-page',
        loadComponent: () => import('./pages/category/category-page/category-page.component').then(m => m.CategoryPageComponent),
        canActivate: [AuthGuard]
    },
    {
        path: 'create-task-category-page',
        loadComponent: () => import('./pages/category/create-category-page/create-category-page.component').then(m => m.CreateCategoryPageComponent),
        canActivate: [AuthGuard]
    },
    {
        path: 'list-task',
        loadComponent: () => import('./pages/task/list-task/list-task.component').then(m => m.ListTaskComponent),
        canActivate: [AuthGuard]
    },
    {
        path: 'list-task/:categoryId',
        loadComponent: () => import('./pages/task/list-task/list-task.component').then(m => m.ListTaskComponent),
        canActivate: [AuthGuard]
    },
    {
        path: 'reset-password-page',
        loadComponent: () => import('./pages/auth/reset-password-page/reset-password-page.component').then(m => m.ResetPasswordPageComponent),
        canActivate: [AuthGuard]
    },
    {
        path: 'personal-coin-record',
        loadComponent: () => import('./pages/finance/personal-coin-record/personal-coin-record.component').then(m => m.PersonalCoinRecordComponent),
        canActivate: [AuthGuard]
    },
    
    // Error and fallback routes
    {
        path: 'error',
        component: ErrorPageComponent
    },
    {
        path: '**',
        component: ErrorPageComponent
    }
];
